import { useEffect, useCallback, useRef } from "react";
import { useTokenRefresh } from "@/hooks/useTokenRefresh";
import useAuthStore from "@/stores/auth";

const TOKEN_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds
const INITIAL_CHECK_DELAY = 10 * 1000; // 10 seconds delay after login before first token check

export const TokenRefreshProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isAuthenticated } = useAuthStore();
  const { refreshTokens, isTokenExpired } = useTokenRefresh();
  const hasInitializedRef = useRef(false);
  const previousAuthRef = useRef(isAuthenticated);

  const checkAndRefreshToken = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      if (isTokenExpired()) {
        await refreshTokens();
      }
    } catch (error) {
      console.error("Failed to refresh token:", error);
    }
  }, [isAuthenticated, isTokenExpired, refreshTokens]);

  useEffect(() => {
    if (!isAuthenticated) {
      // Reset initialization flag when logged out
      hasInitializedRef.current = false;
      previousAuthRef.current = false;
      return;
    }

    // Detect if this is a fresh login (was not authenticated, now is)
    const isFreshLogin = !previousAuthRef.current && isAuthenticated;
    previousAuthRef.current = isAuthenticated;

    let initialCheckTimeout: NodeJS.Timeout | undefined;
    let interval: NodeJS.Timeout | undefined;

    if (isFreshLogin && !hasInitializedRef.current) {
      // For fresh login, delay the first token check to avoid race conditions
      hasInitializedRef.current = true;
      initialCheckTimeout = setTimeout(() => {
        checkAndRefreshToken();
        // Set up interval after the delayed first check
        interval = setInterval(checkAndRefreshToken, TOKEN_REFRESH_INTERVAL);
      }, INITIAL_CHECK_DELAY);
    } else if (hasInitializedRef.current) {
      // Already initialized, just set up the interval
      interval = setInterval(checkAndRefreshToken, TOKEN_REFRESH_INTERVAL);
    } else {
      // Page reload with existing session - check immediately but don't logout on failure
      hasInitializedRef.current = true;
      checkAndRefreshToken();
      interval = setInterval(checkAndRefreshToken, TOKEN_REFRESH_INTERVAL);
    }

    return () => {
      if (initialCheckTimeout) clearTimeout(initialCheckTimeout);
      if (interval) clearInterval(interval);
    };
  }, [isAuthenticated, checkAndRefreshToken]);

  // Also check when the tab becomes visible again
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated) {
        checkAndRefreshToken();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, checkAndRefreshToken]);

  return <>{children}</>;
};

export default TokenRefreshProvider;
